import React, { useState } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Grid,
    Card,
    CardContent,
    Tabs,
    Tab,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,

    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Search as SearchIcon,
    AddPhotoAlternate as AddPhotoIcon,
    AttachMoney as MoneyIcon,
    Chat as ChatIcon,
    StarBorder as StarBorderIcon,
    ShoppingCart as CartIcon,
    CheckCircle as CheckCircleIcon,
    LocalShipping as ShippingIcon,
    CreditCard as PaymentIcon,
    MonetizationOn as EarnIcon,
    Security as SecurityIcon,
    Schedule as ScheduleIcon,
    Photo as PhotoIcon,
    Description as DescriptionIcon,
    Category as CategoryIcon,
    LocationOn as LocationIcon,
    ArrowBackIos as ArrowBackIosIcon,
    ArrowForwardIos as ArrowForwardIosIcon,
    Close as CloseIcon,
    ShoppingCart
} from '@mui/icons-material';

// Styled components
const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(8),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(4),
}));

const InfoCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[6],
    },
}));

const IconBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    '& svg': {
        fontSize: 50,
        color: theme.palette.primary.main,
    },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
    marginBottom: theme.spacing(4),
    '& .MuiTab-root': {
        minWidth: 160,
        fontWeight: 600,
    },
}));

const StepperBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));

const BenefitList = styled(List)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

function HowItWorks() {
    const [tabValue, setTabValue] = useState(0);
    const [galleryOpen, setGalleryOpen] = useState(false);
    const [galleryImages, setGalleryImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleImageClick = (images, index) => {
        setGalleryImages(images);
        setCurrentImageIndex(index);
        setGalleryOpen(true);
    };

    const handleCloseGallery = () => {
        setGalleryOpen(false);
    };

    const handlePrevImage = () => {
        setCurrentImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
    };

    // Step content for borrowers
    const borrowerSteps = [
        {
            label: 'Search for Items',
            icon: <SearchIcon color="primary" />,
            description: 'Browse available items in your area by category, location, or specific search terms. Filter by price, availability dates, and ratings.',
            image: `${process.env.PUBLIC_URL}/images/search.png`
        },
        {
            label: 'Request to Borrow',
            icon: <CartIcon color="primary" />,
            description: "Select your rental dates and send a request to the lender. Include a brief message about how you'll use the item.",
            image: `${process.env.PUBLIC_URL}/images/request.png`
        },
        {
            label: 'Coordinate Pickup/Delivery',
            icon: <ShippingIcon color="primary" />,
            description: 'Arrange with the lender to pick up the item or have it delivered based on their preferences.',
            image: `${process.env.PUBLIC_URL}/images/coordinate.png`
        },
        {
            label: 'Pay Securely',
            icon: <PaymentIcon color="primary" />,
            description: 'Once your request is approved, pay securely through our platform. Your payment is held until you receive the item.',
            image: `${process.env.PUBLIC_URL}/images/pay.png`
        },
        {
            label: 'Use and Return',
            icon: <ScheduleIcon color="primary" />,
            description: 'Enjoy using the item during your rental period. Return it on time and in the same condition as received.',
            image: `${process.env.PUBLIC_URL}/images/use_return.png`
        },
        {
            label: 'Leave a Review',
            icon: <StarBorderIcon color="primary" />,
            description: 'After returning the item, rate your experience and provide feedback about the lender and the item quality.',
            image: `${process.env.PUBLIC_URL}/images/review.png`
        }
    ];

    // Step content for lenders
    const lenderSteps = [
        {
            label: 'Create a Listing',
            icon: <AddPhotoIcon color="primary" />,
            description: 'Upload clear photos and add a detailed description of your item. Set rental prices, availability, and any specific terms.',
            image: `${process.env.PUBLIC_URL}/images/create.png`
        },
        {
            label: 'Receive Requests',
            icon: <ChatIcon color="primary" />,
            description: 'Get notified when someone wants to borrow your item. Review their profile and rental request details.',
            image: `${process.env.PUBLIC_URL}/images/receive_requests.png`
        },
        {
            label: 'Accept and Coordinate',
            icon: <CheckCircleIcon color="primary" />,
            description: "Approve requests from borrowers you're comfortable with. Arrange pick-up or delivery details through our messaging system.",
            image: `${process.env.PUBLIC_URL}/images/coordinate.png`
        },
        {
            label: 'Hand Over the Item',
            icon: <ShippingIcon color="primary" />,
            description: 'Meet the borrower or arrange delivery. Ensure the item is clean and functioning properly before the rental begins.',
            image: `${process.env.PUBLIC_URL}/images/handover.png`
        },
        {
            label: 'Get Your Item Back',
            icon: <CheckCircleIcon color="primary" />,
            description: 'The borrower returns your item at the agreed time. Check that everything is in good condition and resolve any issues through our platform.',
            image: `${process.env.PUBLIC_URL}/images/get_item_back.png`
        },
        {
            label: 'Receive Payment',
            icon: <MoneyIcon color="primary" />,
            description: "Once the rental period ends, the payment will be processed. You'll receive the funds in your account after the rental service fee is deducted.",
            image: `${process.env.PUBLIC_URL}/images/receive_payment.png`
        }
    ];

    // Benefits content
    const borrowerBenefits = [
        {
            title: 'Save Money',
            icon: <MoneyIcon color="primary" />,
            description: 'Pay a fraction of the purchase price for items you only need temporarily.'
        },
        {
            title: 'Reduce Clutter',
            icon: <CheckCircleIcon color="primary" />,
            description: 'Access items without having to store them long-term in your home.'
        },
        {
            title: 'Try Before You Buy',
            icon: <ShoppingCart color="primary" />,
            description: 'Test products before committing to a purchase.'
        },
        {
            title: 'Support Sustainability',
            icon: <SecurityIcon color="primary" />,
            description: 'Reduce waste and your environmental footprint by borrowing instead of buying.'
        }
    ];

    const lenderBenefits = [
        {
            title: 'Earn Extra Income',
            icon: <EarnIcon color="primary" />,
            description: "Make money from items you already own when you're not using them"
        },
        {
            title: 'Offset Purchase Costs',
            icon: <MoneyIcon color="primary" />,
            description: 'Recover part of your investment in expensive items through rental income.'
        },
        {
            title: 'Utilize Idle Items',
            icon: <ScheduleIcon color="primary" />,
            description: 'Put rarely-used possessions to work instead of letting them collect dust.'
        },
        {
            title: 'Build Community',
            icon: <SecurityIcon color="primary" />,
            description: 'Help neighbors access items they need while building local connections.'
        }
    ];

    // FAQ content
    // const faqs = [
    //     {
    //         question: 'What if an item gets damaged during rental?',
    //         answer: 'Borrowed offers Owner Protection that may cover qualifying damage. When a problem occurs, we help facilitate a resolution between lender and borrower. Borrowers are responsible for returning items in the condition they received them, and may be charged for damages.'
    //     },
    //     {
    //         question: 'How does payment work?',
    //         answer: 'Borrowers pay through our secure platform when a rental request is accepted. The payment is held securely until the borrower receives the item. Lenders receive the rental amount minus a 14% service fee. Payments are processed through Stripe.'
    //     },
    //     {
    //         question: 'What items can I rent or lend?',
    //         answer: 'Most household items, tools, sports equipment, electronics, and more can be rented. Prohibited items include weapons, illegal items, counterfeit goods, alcohol/tobacco products, and personal items like clothing or cosmetics.'
    //     },
    //     {
    //         question: 'How is my safety and privacy protected?',
    //         answer: "All users must verify their identity through our platform. We provide secure messaging so you don\\'t need to share personal contact information.Our rating system helps build trust, and our terms of service govern appropriate conduct."
    //     },
    //     {
    //         question: 'What happens if the borrower keeps my item?',
    //         answer: "If an item isn't returned on time, late fees will automatically apply.For significantly overdue items, we\\'ll help with the recovery process and may charge the borrower up to the item\\'s fair market value."
    //     },
    //     {
    //         question: 'Can I cancel a rental?',
    //         answer: 'Yes, but cancellation policies vary based on timing. For lenders, cancelling an accepted booking may incur a 30% cancellation fee. Borrowers may receive a full or partial refund depending on how close to the rental period they cancel.'
    //     }
    // ];

    // Tips content
    const borrowerTips = [
        'Read the full item description and rules before requesting',
        'Ask questions about the item before booking',
        'Take photos of the item when you receive it to document condition',
        'Communicate any delays or issues promptly',
        'Clean the item before returning it',
        'Return on time to avoid late fees'
    ];

    const lenderTips = [
        'Take clear, well-lit photos from multiple angles',
        'Be detailed and honest in your item description',
        "Set fair pricing based on the item\\'s value",
        'Respond promptly to rental requests',
        'Provide clear instructions for using your item',
        'Inspect items thoroughly before and after rental'
    ];

    // Listing tips content
    const listingTips = [
        {
            title: 'High-Quality Photos',
            icon: <PhotoIcon color="primary" />,
            description: 'Upload 3-5 clear photos showing the item from different angles in good lighting. Include photos of any unique features or minor defects.'
        },
        {
            title: 'Detailed Description',
            icon: <DescriptionIcon color="primary" />,
            description: 'Write a thorough description including brand, model, dimensions, condition, and special features. Mention any quirks or specific instructions for use.'
        },
        {
            title: 'Accurate Categorization',
            icon: <CategoryIcon color="primary" />,
            description: 'Select the most appropriate category and subcategory to help borrowers find your item when searching.'
        },
        {
            title: 'Clear Location',
            icon: <LocationIcon color="primary" />,
            description: 'Specify your neighborhood or general area (not your exact address) so borrowers know if your item is conveniently located.'
        }
    ];

    return (
        <StyledContainer maxWidth="lg">
            <Typography variant="h4" component="h1" align="center" gutterBottom>
                How Borrowed Works
            </Typography>

            <Typography variant="subtitle1" align="center" color="textSecondary" paragraph>
                Your comprehensive guide to borrowing and lending on our platform
            </Typography>

            <StyledPaper>
                <StyledTabs
                    value={tabValue}
                    onChange={handleTabChange}
                    centered
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label="For Borrowers" />
                    <Tab label="For Lenders" />
                </StyledTabs>

                {/* Borrower Content */}
                <div role="tabpanel" hidden={tabValue !== 0}>
                    {tabValue === 0 && (
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Borrowing Made Simple
                            </Typography>
                            <Typography variant="body1" paragraph>
                                Need something temporarily? Borrowed connects you with people in your community who are willing to lend items for a fee. Follow these steps to start borrowing:
                            </Typography>

                            {/* Process Steps for Borrowers */}
                            <StepperBox>
                                <Stepper orientation="vertical">
                                    {borrowerSteps.map((step, index) => (
                                        <Step key={index} active={true}>
                                            <StepLabel StepIconComponent={() => step.icon}>
                                                <Typography variant="h6">{step.label}</Typography>
                                            </StepLabel>
                                            <StepContent>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} md={8}>
                                                        <Typography>{step.description}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <Box
                                                            component="img"
                                                            src={step.image}
                                                            alt={`Step ${index + 1} illustration`}
                                                            sx={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                maxHeight: 140,
                                                                objectFit: 'contain',
                                                                borderRadius: 1,
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => handleImageClick(borrowerSteps.map((s) => s.image), index)}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </StepContent>
                                        </Step>
                                    ))}
                                </Stepper>
                            </StepperBox>

                            <Divider sx={{ my: 4 }} />

                            {/* Benefits of Borrowing */}
                            <Typography variant="h5" gutterBottom>
                                Benefits of Borrowing
                            </Typography>
                            <Grid container spacing={3}>
                                {borrowerBenefits.map((benefit, index) => (
                                    <Grid item xs={12} sm={6} md={3} key={index}>
                                        <InfoCard>
                                            <CardContent>
                                                <IconBox>
                                                    {benefit.icon}
                                                </IconBox>
                                                <Typography variant="h6" align="center" gutterBottom>
                                                    {benefit.title}
                                                </Typography>
                                                <Typography variant="body2" align="center">
                                                    {benefit.description}
                                                </Typography>
                                            </CardContent>
                                        </InfoCard>
                                    </Grid>
                                ))}
                            </Grid>

                            <Divider sx={{ my: 4 }} />

                            {/* Tips for Successful Borrowing */}
                            <Typography variant="h5" gutterBottom>
                                Tips for Successful Borrowing
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Best Practices
                                        </Typography>
                                        <List>
                                            {borrowerTips.map((tip, index) => (
                                                <ListItem key={index}>
                                                    <ListItemIcon>
                                                        <CheckCircleIcon color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={tip} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                        <Typography variant="h6" gutterBottom>
                                            Did You Know?
                                        </Typography>
                                        <Typography variant="body1" paragraph>
                                            The average household has thousands of dollars worth of items that are used less than once a month. By borrowing instead of buying, you can:
                                        </Typography>
                                        <BenefitList>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <CheckCircleIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText primary="Save up to 80% compared to buying new" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <CheckCircleIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText primary="Reduce your environmental footprint" />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <CheckCircleIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText primary="Free up storage space in your home" />
                                            </ListItem>
                                        </BenefitList>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </div>

                {/* Lender Content */}
                <div role="tabpanel" hidden={tabValue !== 1}>
                    {tabValue === 1 && (
                        <Box>
                            <Typography variant="h5" gutterBottom>
                                Turn Your Idle Items Into Income
                            </Typography>
                            <Typography variant="body1" paragraph>
                                Have items you don\'t use every day? Borrowed lets you earn money by lending them to people in your community. Here\'s how to get started as a lender:
                            </Typography>

                            {/* Process Steps for Lenders */}
                            <StepperBox>
                                <Stepper orientation="vertical">
                                    {lenderSteps.map((step, index) => (
                                        <Step key={index} active={true}>
                                            <StepLabel StepIconComponent={() => step.icon}>
                                                <Typography variant="h6">{step.label}</Typography>
                                            </StepLabel>
                                            <StepContent>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} md={8}>
                                                        <Typography>{step.description}</Typography>
                                                    </Grid>
                                                    <Grid item xs={12} md={4}>
                                                        <Box
                                                            component="img"
                                                            src={step.image}
                                                            alt={`Step ${index + 1} illustration`}
                                                            sx={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                maxHeight: 140,
                                                                objectFit: 'contain',
                                                                borderRadius: 1,
                                                                cursor: 'pointer'
                                                            }}
                                                            onClick={() => handleImageClick(borrowerSteps.map((s) => s.image), index)}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </StepContent>
                                        </Step>
                                    ))}
                                </Stepper>
                            </StepperBox>

                            <Divider sx={{ my: 4 }} />

                            {/* Benefits of Lending */}
                            <Typography variant="h5" gutterBottom>
                                Benefits of Lending
                            </Typography>
                            <Grid container spacing={3}>
                                {lenderBenefits.map((benefit, index) => (
                                    <Grid item xs={12} sm={6} md={3} key={index}>
                                        <InfoCard>
                                            <CardContent>
                                                <IconBox>
                                                    {benefit.icon}
                                                </IconBox>
                                                <Typography variant="h6" align="center" gutterBottom>
                                                    {benefit.title}
                                                </Typography>
                                                <Typography variant="body2" align="center">
                                                    {benefit.description}
                                                </Typography>
                                            </CardContent>
                                        </InfoCard>
                                    </Grid>
                                ))}
                            </Grid>

                            <Divider sx={{ my: 4 }} />

                            {/* Creating a Great Listing */}
                            <Typography variant="h5" gutterBottom>
                                Creating a Great Listing
                            </Typography>
                            <Grid container spacing={3}>
                                {listingTips.map((tip, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                        <InfoCard>
                                            <CardContent>
                                                <Box display="flex" alignItems="center" mb={2}>
                                                    {tip.icon}
                                                    <Typography variant="h6" sx={{ ml: 2 }}>
                                                        {tip.title}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2">
                                                    {tip.description}
                                                </Typography>
                                            </CardContent>
                                        </InfoCard>
                                    </Grid>
                                ))}
                            </Grid>

                            <Divider sx={{ my: 4 }} />

                            {/* Tips for Successful Lending */}
                            <Typography variant="h5" gutterBottom>
                                Tips for Successful Lending
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Best Practices
                                        </Typography>
                                        <List>
                                            {lenderTips.map((tip, index) => (
                                                <ListItem key={index}>
                                                    <ListItemIcon>
                                                        <CheckCircleIcon color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={tip} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
                                        <Typography variant="h6" gutterBottom>
                                            Earning Potential
                                        </Typography>
                                        <Typography variant="body1" paragraph>
                                            Popular items on Borrowed can earn their owners significant income:
                                        </Typography>
                                        <BenefitList>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <EarnIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary="Power Tools"
                                                    secondary="Can earn 30-50% of their purchase price annually"
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <EarnIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary="Photography Equipment"
                                                    secondary="Can recover up to 70% of initial investment in a year"
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <EarnIcon color="success" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary="Outdoor Gear"
                                                    secondary="Can recoup purchase price in 10-15 rentals"
                                                />
                                            </ListItem>
                                        </BenefitList>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </div>


                {/* Image Gallery Dialog */}
                <Dialog open={galleryOpen} onClose={handleCloseGallery} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ p: 0 }}>
                        <IconButton aria-label="close" onClick={handleCloseGallery} sx={{ position: 'absolute', right: 8, top: 8 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', p: 0 }}>
                        <IconButton onClick={handlePrevImage} sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }}>
                            <ArrowBackIosIcon />
                        </IconButton>
                        <Box
                            component="img"
                            src={galleryImages[currentImageIndex]}
                            alt={`Gallery image ${currentImageIndex + 1}`}
                            sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
                        />
                        <IconButton onClick={handleNextImage} sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
                            <ArrowForwardIosIcon />
                        </IconButton>
                    </DialogContent>
                </Dialog>

                {/* Call to Action */}
                <Box textAlign="center" mt={6}>
                    <Typography variant="h5" gutterBottom>
                        Ready to Get Started?
                    </Typography>
                    <Box display="flex" justifyContent="center" gap={2} mt={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={() => window.location.href = '/listings'}
                        >
                            Browse Items
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => window.location.href = '/create-listing'}
                        >
                            List an Item
                        </Button>
                    </Box>
                </Box>
            </StyledPaper>
        </StyledContainer>
    );
}

export default HowItWorks;